import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { Role } from "@/types/api";
import { Login } from "@/pages/auth/Login";
import { NewPassword } from "@/pages/auth/NewPassword";
import { Onboarding } from "@/pages/auth/Onboarding";

// Lazy: the console pulls in Cloudscape (see pages/console/ConsoleRoot.tsx), so it
// must never be part of the entry chunk a phone downloads for /student or /teacher.
const ConsoleRoot = lazy(() => import("@/pages/console/ConsoleRoot"));
const StudentRoot = lazy(() => import("@/pages/student/StudentRoot"));
const TeacherRoot = lazy(() => import("@/pages/teacher/TeacherRoot"));

// eslint-disable-next-line react-refresh/only-export-components
export function homeFor(role: Role): string {
  if (role === "ADMIN" || role === "LIBRARIAN") return "/console";
  if (role === "TEACHER") return "/teacher";
  return "/student";
}

function FullScreenLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center text-gray-400">
      <p>Wird geladen …</p>
    </div>
  );
}

/** Gate: not signed in -> /login; needs_contact -> /onboarding. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === "loading") return <FullScreenLoading />;
  if (state.status === "new-password-required") return <Navigate to="/login/new-password" replace />;
  if (state.status !== "signed-in") return <Navigate to="/login" replace />;
  if (state.me.needs_contact) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

/** Wrong role for a path -> redirect to the user's own home (docs/SPEC.md section 5). */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { state } = useAuth();
  if (state.status !== "signed-in") return null;
  if (!roles.includes(state.role)) return <Navigate to={homeFor(state.role)} replace />;
  return <>{children}</>;
}

function Home() {
  const { state } = useAuth();
  if (state.status === "loading") return <FullScreenLoading />;
  if (state.status === "new-password-required") return <Navigate to="/login/new-password" replace />;
  if (state.status !== "signed-in") return <Navigate to="/login" replace />;
  if (state.me.needs_contact) return <Navigate to="/onboarding" replace />;
  return <Navigate to={homeFor(state.role)} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/login/new-password" element={<NewPassword />} />
      <Route
        path="/onboarding"
        element={
          <RequireAuthForOnboarding>
            <Onboarding />
          </RequireAuthForOnboarding>
        }
      />
      <Route
        path="/console/*"
        element={
          <RequireAuth>
            <RequireRole roles={["ADMIN", "LIBRARIAN"]}>
              <Suspense fallback={<FullScreenLoading />}>
                <ConsoleRoot />
              </Suspense>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/student/*"
        element={
          <RequireAuth>
            <RequireRole roles={["STUDENT"]}>
              <Suspense fallback={<FullScreenLoading />}>
                <StudentRoot />
              </Suspense>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="/teacher/*"
        element={
          <RequireAuth>
            <RequireRole roles={["TEACHER"]}>
              <Suspense fallback={<FullScreenLoading />}>
                <TeacherRoot />
              </Suspense>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Onboarding only needs a signed-in session, not a completed one (that's the point). */
function RequireAuthForOnboarding({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === "loading") return <FullScreenLoading />;
  if (state.status !== "signed-in") return <Navigate to="/login" replace />;
  return <>{children}</>;
}
